/**
 * Sets the uID and hpo_uID cookies in the browser.
 * Sends user profile data to Connector Grid, if available at a browser-level.
 *
 * @async
 * @function
 * @name getUniversalIdCookie
 * @param {ResponseBody} userProfileData
 * @returns {Promise<void|any>} - A promise that resolves once user profile data is sent.
 *
 * @author Jaskiran Chhokar
 */

import apiConfig from '../api/apiConfig.ts';
import { ResponseBody } from 'globalTypes';
import connectorGridApiHeader from '../constants/connectorGridApiHeader.ts';

/**
 * This code manually sets the uID cookie in the browser, it can be uncommented if needed.
 *
 * import getMainDomainFromCurrentUrl from '../collectors/collectDomain.ts';
 * const setUniversalIdCookie = (cookieName: string, cookieValue?: string): string => {
 *   const domain = getMainDomainFromCurrentUrl();
 *   const cookieString = `${cookieName} = ${(cookieValue || '')}; Domain=${domain}; Max-Age=86400; Path=/; SameSite=None; Secure`;
 *   return document.cookie = cookieString;
 * };
 */

const getUniversalIdCookie = async (userProfileData: ResponseBody, xCryptoKey?: string) => {
    const userProfileDataWithoutUID = JSON.parse(JSON.stringify(userProfileData));
    delete userProfileDataWithoutUID.universalIds.uID;
    try {
        // if SECURED.encrypted= true then the x-crypto-key should be updated
        const newHeader = { ...connectorGridApiHeader, ...(xCryptoKey ? { 'x-crypto-key': xCryptoKey } : '') };
        const response = await fetch(apiConfig.connectorGrid.getUIDCookie, {
            method: 'POST',
            credentials: 'include',
            headers: newHeader,
            body: JSON.stringify(userProfileDataWithoutUID),
        });
        if (!response.ok) {
            throw new Error(`Network request to Connector Grid failed! Received status code: ${response.status}`);
        }
        return await response.json();
        // To manually set the uID cookie in the browser, uncomment the following line:
        // setUniversalIdCookie('uID', data.uID);
    } catch (error) {
        const err = error as Error;
        console.error(`Error in getUniversalIdCookie: ${err.message || err}`);
    }
};

export default getUniversalIdCookie;
